import { z } from 'zod';
import { PlanStatus } from './team.js';

/** Where a personal plan came from. Stripe is the web; Apple and Google arrive with the mobile apps; grant is ours. */
export const PlanSource = z.enum(['trial', 'stripe', 'apple', 'google', 'grant']);
export type PlanSource = z.infer<typeof PlanSource>;

/** Everything Timato sells is priced in euros: the company banks in EUR, and Stripe adds 2% for settling into another currency. */
export const CURRENCY = 'eur';
/** Team, per seat per month, in CURRENCY. The Stripe Price has to say the same; the Upgrade page shows it before any team exists. */
export const SEAT_PRICE_CENTS = 400;
/**
 * Personal, in CURRENCY, and its free days. Shared rather than the server's own, so the landing page, its structured
 * data and the plan summary can never quote different prices (TW-60). The Stripe Prices have to say the same.
 */
export const PERSONAL_MONTHLY_CENTS = 300;
export const PERSONAL_YEARLY_CENTS = 3000;
export const PERSONAL_TRIAL_DAYS = 7;
export const PlanInterval = z.enum(['month', 'year']);
export type PlanInterval = z.infer<typeof PlanInterval>;

/**
 * The person's standing, as the client needs it: are the paid features on, and why. `via: 'team'` means a live
 * Team plan is carrying them, so there is nothing personal to manage. `managedExternally` means the subscription
 * lives with an app store and cannot be changed from the web. `teamTrialAvailable` is false once the person has
 * started a team before: a new team then starts without the 7-day trial (TW-16).
 */
export const PlanSummary = z.object({
  entitled: z.boolean(),
  via: z.enum(['plan', 'team']).nullable(),
  status: PlanStatus.nullable(),
  source: PlanSource.nullable(),
  currentPeriodEnd: z.string().nullable(),
  hasSubscription: z.boolean(),
  managedExternally: z.boolean(),
  teamTrialAvailable: z.boolean(),
  prices: z.object({ monthlyCents: z.number().int(), yearlyCents: z.number().int(), currency: z.string() }),
});
export type PlanSummary = z.infer<typeof PlanSummary>;

export const PersonalCheckoutRequest = z.object({ interval: PlanInterval });
